import i18n from '@/i18n'
import { auth } from '@/plugins/firebaseClient'
import { signInWithEmailAndPassword, signOut } from 'firebase/auth'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useUserStore } from './user'

export const useAuthStore = defineStore('auth', () => {
	// State
	const isInitialized = ref(false)
	const user = ref(null)
	const error = ref(null)
	const userStore = useUserStore()

	// Actions
	async function initializeUser(user) {
		if (!user) {
			userStore.userLoggedOut()
			return
		}

		userStore.modifySpinnerStatus(true)

		try {
			const functions = getFunctions()
			const callFirebase = httpsCallable(functions, 'customRestAPI')
			const isNationalAccountWCC = userStore.countryVersion === 'CAN'

			if (isNationalAccountWCC) {
				const result = await callFirebase({
					typeOfAction: 'getBasicUserInfo',
					uid: user.uid,
					isNationalAccountWCC: true,
				})
				const data = JSON.parse(result.data)
				await initializeCanadianUser(user, data, userStore)
			} else {
				const result = await callFirebase({
					typeOfAction: 'getOrganigramStructure',
					isNationalAccountWCC: false,
				})
				const data = JSON.parse(result.data)
				await userStore.initializeUSUser(user, data)
			}

			isInitialized.value = true
		} catch (err) {
			console.error('Error initializing user:', err)
			throw err
		} finally {
			userStore.modifySpinnerStatus(false)
		}
	}

	async function login(email, password) {
		try {
			userStore.modifySpinnerStatus(true)
			await signInWithEmailAndPassword(auth, email, password)
		} catch (err) {
			error.value = err.message
			console.error('Error signing in:', err)
			throw err
		} finally {
			userStore.modifySpinnerStatus(false)
		}
	}

	async function logout() {
		try {
			userStore.modifySpinnerStatus(true)
			await signOut(auth)
			user.value = null
			userStore.userLoggedOut()
		} catch (err) {
			error.value = err.message
			console.error('Error signing out:', err)
			throw err
		} finally {
			userStore.modifySpinnerStatus(false)
		}
	}

	async function initializeCanadianUser(user, data, userStore) {
		try {
			let manageUserOptions = []
			if (
				[
					'WCN Admin',
					'WCN National Account Admin',
					'WCN National Account Manager',
					'WCN District Manager',
					'External Admin',
				].includes(data.userType)
			) {
				manageUserOptions = [
					{ title: i18n.t('create'), link: '/create-user' },
					{ title: i18n.t('update'), link: '/update-user/false/false' },
					{ title: i18n.t('copy'), link: '/copy-transfer-user/copy/false/false' },
					{ title: i18n.t('transfer'), link: '/copy-transfer-user/transfer/false/false' },
				]
				if (data.userType === 'WCN Admin') {
					manageUserOptions.push({ title: i18n.t('delete'), link: '/DeleteUser/false/false' })
				}
			}

			userStore.setManageCustomerOptions()

			userStore.modifyUserInfo({
				name: user.displayName,
				email: user.email,
				isLogged: true,
				isLoaded: true,
				uid: user.uid,
				type: data.userType,
				manageUserOptions,
				isNationalAccountsUser: data.userIsNationalAccounts,
				showDistrictLevel: data.showDistrictLevel,
				availableDistricts: data.availableDistricts,
			})
		} catch (err) {
			console.error('Error in initializeCanadianUser:', err)
			throw err
		}
	}

	return {
		// State
		user,
		error,
		// Actions
		initializeUser,
		login,
		logout,
	}
})
